import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Trash2, 
  Edit,
  Calendar,
  Tag,
  Sun,
  Moon,
  Cloud,
  Smile,
  Meh,
  ChevronLeft,
  ShoppingBag
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const TRUENORTH_STORE_URL = "https://www.truenorth-zone.com/collections";

const MOODS = [
  { value: "calm", label: "Calm", icon: Sun, color: "mood-calm" },
  { value: "reflective", label: "Reflective", icon: Moon, color: "mood-reflective" },
  { value: "anxious", label: "Anxious", icon: Cloud, color: "mood-anxious" },
  { value: "hopeful", label: "Hopeful", icon: Smile, color: "mood-hopeful" },
  { value: "uncertain", label: "Uncertain", icon: Meh, color: "mood-uncertain" }
];

const TAGS = [
  "identity", "fear", "clarity", "boundaries", "purpose", "patterns", 
  "growth", "release", "insight", "gratitude"
];

export default function JournalPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAllEntries, setShowAllEntries] = useState(false);
  
  // Form state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mood, setMood] = useState("");
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      let url = `${API}/journal`;
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (selectedTag && selectedTag !== "all") params.append("tag", selectedTag);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        withCredentials: true
      });
      setEntries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Journal error:", error);
      // Only show error toast for genuine failures, not initial auth issues
      if (error.response?.status !== 401) {
        toast.error("Failed to load journal entries");
      }
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedTag]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const resetForm = () => {
    setTitle("");
    setBody("");
    setMood("");
    setTags([]);
    setEditingEntry(null);
  };

  const handleOpenSheet = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setTitle(entry.title);
      setBody(entry.body);
      setMood(entry.mood || "");
      setTags(entry.tags || []);
    } else {
      resetForm();
    }
    setIsSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    resetForm();
  };

  const handleSave = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Please fill in title and body");
      return;
    }

    setSaving(true);
    try {
      if (editingEntry) {
        await axios.put(`${API}/journal/${editingEntry.entry_id}`, {
          title,
          body,
          mood,
          tags
        }, { withCredentials: true });
        toast.success("Entry updated");
      } else {
        await axios.post(`${API}/journal`, {
          title,
          body,
          mood,
          tags
        }, { withCredentials: true });
        toast.success("Entry created");
      }
      handleCloseSheet();
      fetchEntries();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm("Are you sure you want to delete this entry?")) return;
    
    try {
      await axios.delete(`${API}/journal/${entryId}`, { withCredentials: true });
      toast.success("Entry deleted");
      fetchEntries();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete entry");
    }
  };

  const toggleTag = (tag) => {
    setTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const getMoodData = (moodValue) => {
    return MOODS.find(m => m.value === moodValue);
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="px-4 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate(-1)}
                className="w-10 h-10 rounded-full bg-emerald-dark/10 flex items-center justify-center"
              >
                <ChevronLeft className="w-5 h-5 text-foreground" />
              </button>
              <h1 className="text-2xl font-heading font-bold" style={{ color: '#a69373' }}>Journal</h1>
            </div>
            <div className="flex items-center gap-2">
              <a 
                href={TRUENORTH_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#a69373]/20 text-[#a69373] text-sm font-medium hover:bg-[#a69373]/30 transition-colors"
              >
                <ShoppingBag className="w-4 h-4" />
                Shop
              </a>
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button 
                    className="btn-primary"
                    onClick={() => handleOpenSheet()}
                    data-testid="new-entry-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  New
                </Button>
              </SheetTrigger>
              
              <SheetContent className="bg-card border-white/10 w-full sm:max-w-lg overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="text-foreground">
                    {editingEntry ? "Edit Entry" : "New Journal Entry"}
                  </SheetTitle>
                </SheetHeader>
                
                <div className="space-y-5 pt-6">
                  <div className="space-y-2">
                    <Label className="text-foreground">Title</Label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Give your entry a title..."
                      className="input-field"
                      data-testid="entry-title-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-foreground">How are you feeling?</Label>
                    <div className="flex flex-wrap gap-2">
                      {MOODS.map((m) => (
                        <Button
                          key={m.value}
                          type="button"
                          variant={mood === m.value ? "default" : "outline"}
                          size="sm"
                          className={mood === m.value ? "bg-secondary" : "border-white/10"}
                          onClick={() => setMood(m.value)}
                        >
                          <m.icon className="w-4 h-4 mr-1" />
                          {m.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-foreground">Your thoughts</Label>
                    <Textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Write freely..."
                      className="min-h-[150px] bg-input border-white/10 focus:border-primary text-foreground"
                      data-testid="entry-body-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-foreground">Tags</Label>
                    <div className="flex flex-wrap gap-2">
                      {TAGS.map((tag) => (
                        <Button
                          key={tag}
                          type="button"
                          variant={tags.includes(tag) ? "default" : "outline"}
                          size="sm"
                          className={tags.includes(tag) ? "bg-primary text-primary-foreground" : "border-white/10"}
                          onClick={() => toggleTag(tag)}
                        >
                          {tag}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1 border-white/10"
                      onClick={handleCloseSheet}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="btn-primary flex-1"
                      onClick={handleSave}
                      disabled={saving}
                      data-testid="save-entry-btn"
                    >
                      {saving ? "Saving..." : (editingEntry ? "Update" : "Save Entry")}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            </div>
          </div>
        </div>
      </div>

      <main className="px-4 py-6 max-w-5xl mx-auto">
        {/* Filters */}
        <section className="mb-6 animate-fade-in" style={{ animationDelay: "100ms" }}>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search entries..."
                className="input-field pl-12"
                data-testid="search-input"
              />
            </div>
            
            <Select value={selectedTag} onValueChange={setSelectedTag}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-white/10 text-foreground">
                <Tag className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Filter by tag" />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                <SelectItem value="all">All tags</SelectItem>
                {TAGS.map((tag) => (
                  <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Entries List */}
        <section className="space-y-4" data-testid="entries-list">
          {loading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-muted rounded-xl" />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground mb-4">No journal entries yet.</p>
                <Button 
                  className="btn-primary"
                  onClick={() => handleOpenSheet()}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Your First Entry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {(showAllEntries ? entries : entries.slice(0, 5)).map((entry, index) => {
                const moodData = getMoodData(entry.mood);
                
                return (
                  <Card 
                    key={entry.entry_id}
                    className="glass-card card-hover animate-fade-in"
                    style={{ animationDelay: `${(index + 2) * 50}ms` }}
                    data-testid={`entry-${index}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-foreground font-medium text-lg">{entry.title}</h3>
                            {moodData && (
                              <span className={`mood-badge ${moodData.color}`}>
                                <moodData.icon className="w-3 h-3 mr-1" />
                                {moodData.label}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-muted-foreground line-clamp-2 mb-3">{entry.body}</p>
                          
                          <div className="flex items-center gap-4 flex-wrap">
                            <span className="text-muted-foreground text-sm flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(entry.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric"
                              })}
                            </span>
                            
                            {entry.tags?.length > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {entry.tags.map((tag) => (
                                  <span key={tag} className="tag-badge">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => handleOpenSheet(entry)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-muted-foreground hover:text-red-400"
                            onClick={() => handleDelete(entry.entry_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {entries.length > 5 && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowAllEntries(!showAllEntries)}
                  data-testid="toggle-entries"
                >
                  {showAllEntries ? (
                    <>Show Less</>
                  ) : (
                    <>Show {entries.length - 5} More Entries</>
                  )}
                </Button>
              )}
            </>
          )}
        </section>
      </main>
      
      <BottomNavigation />
    </div>
  );
}
